import { useEffect, useState, createContext, useContext } from "react";
import moment from "moment";
import { AppContext } from "context/AppContext";
import { useTranslation } from "react-i18next";
import { useFlags } from "launchdarkly-react-client-sdk";
import InviteAssistant from "components/InviteAssistant/InviteAssistant";
import ManageMyTeam from "components/ManageMyTeam/ManageMyTeam";
import { 
    getTeamMembers, 
    getMemberships,
    acceptMembership,
    getPendingMembership, 
    declineMembership } from "api/team";
import AssistantMyTeam from "components/AssistantMyTeam/AssistantMyTeam";
import ConfirmAssistantModal from "components/ConfirmAssistantModal/ConfirmAssistantModal"
import Button from "common/Button/Button";
import AssistAdvisor from "components/AssistAdvisor/AssistAdvisor";


const STATUS_MAP = {
    // pl status
    INVT: "Invited",
    EXPD: "Expired",
    // okta status
    ACTIVE: "Active",
    DEPROVISIONED: "Deprovisioned",
    LOCKED_OUT: "Locked Out",
    PASSWORD_EXPIRED: "Password Expired",
    PROVISIONED: "Provisioned",
    RECOVERY: "Recovery",
    SDK_UNKNOWN: "SDK Unknown",
    STAGED: "Staged",
    SUSPENDED: "Suspended"
};

const formatTeamList = (data) => {
    return [...data].map((m) => {
        m.status = STATUS_MAP[m.status];
        m.name = `${m.firstName} ${m.lastName}`;
        let formattedDate = m.lastLoginDateTime;

        if (formattedDate !== "-" && formattedDate !== undefined) {
            const tz = m.lastLoginDateTime.split(" ")[4];
            formattedDate = `${moment(m.lastLoginDateTime).format(
                "MM/DD/YY, h:mma"
            )} ${tz}`;
        }
        m.lastLoginDateTime = formattedDate;
        return m;
    });
};

const TeamContext = createContext();
export const useTeamContext = () => useContext(TeamContext);

const MyTeam = ({ tab }) => {
    const { user, fetchUserSessionData } = useContext(AppContext);
    const { t } = useTranslation("manMyTeamAssistant");
    const { rsdUpReadonlyaccessEn } = useFlags();

    const [team, setTeam] = useState([]);
    const [membership, setMembership] = useState([]);
    const [loading, setLoading] = useState(false);
    const [modal, setModal] = useState(false);
    const [memberId, setMemberId] = useState("");
    const [inviterName, setInviterName] = useState("");
    const [pending, setPending] = useState("");
    const [error, setError] = useState("");
    const userType = user.custom.type;
    
    const init = async () => {
        try {
            const { succeed, message, data } = await getTeamMembers();
            setLoading(false);
            if (succeed) {
                const teamList = formatTeamList(data);
                setTeam(teamList);
                setError("");
            } else {
                setError(message);
            }
        } catch (err) {
            console.error(err);
        }
    };

    const initMembers = async () => {        
        try {
            const { succeed, message, data } = await getMemberships();
            setLoading(false);
            if (succeed) {
                const membershipList = formatTeamList(data);
                setMembership(membershipList);
                setError("");
            } else {
                setError(message);
            }
        } catch (err) {
            console.error(err);
        }
    };

    const checkForPending = async () => {
        try {
            const { succeed, message, data } = await getPendingMembership();
            setLoading(false);
            if (succeed) {
                setPending(data[0]);
                setError("");
            } else {
                setError(message);
            }
        } catch (err) {
            console.error(err);
        }
    };


    useEffect(() => {
        checkForPending();
        
        if (pending !== "") {
            openModal(true);
            setInviterName(`${pending.firstName} ${pending.lastName}`);
        } else {
            if (team.length === 0) {
                init();
            }
            if (membership.length === 0) {
                initMembers();
            }
        }
    }, [pending]);

    const openModal = (modal) => {
        setModal(modal);
        setMemberId(memberId);
    };

    const closeModal = () => {
        setModal(false);
        setMemberId("");
        setError("");
    };

    const handleAccept = async (e) => {
        setLoading(true);
        const { succeed, message } = await acceptMembership(pending.sysGenId);

        if (succeed) {
            initMembers();
            updateProfileUser();
            closeModal();
        } else {
            setError(message);
        }
        setLoading(false);
    };

    const updateProfileUser = async () => {
        try {
            fetchUserSessionData()
        } catch (err) {
            console.error(err);
        }
    };

    const handleDecline = async (e) => {
        setLoading(true);
        const { succeed, message } = await declineMembership(pending.sysGenId);

        if (succeed) {
            setTeam((prev) => {
                return prev.filter((member) => member.id !== memberId);
            });
            closeModal();
        } else {
            setError(message);
        }
        setLoading(false);
    };

    return (
        <TeamContext.Provider
            value={{
                team,
                setTeam,
                membership, 
                setMembership,
                loading,
                error
            }}
        >            
            <div>
                { tab === 'team' && (
                    userType === 'A' ? 
                        <>
                            <ManageMyTeam />
                            <InviteAssistant />
                        </>
                        : <AssistantMyTeam />
                    )
                }
                { tab === 'select' && (
                        <AssistAdvisor/>
                    )
                }
            </div>

            {modal && (
                <ConfirmAssistantModal
                    disableOutsideClick
                    title={t(`pending.inviteConfirm.title`)}
                    subtitle={t(`pending.inviteConfirm.subtitle`, { value: inviterName })}
                    instructions={t(`pending.inviteConfirm.instructions`)}
                    primaryButton={
                        <Button
                            variant="primary"
                            onClick={handleAccept}
                            data-track="analytics"
                            data-bname={`accept`}
                            disabled={rsdUpReadonlyaccessEn}
                        >
                            {t(`pending.button.accept`)}
                        </Button>
                    }
                    secondaryButton={
                        <Button
                            variant="secondaryNeutral"
                            onClick={handleDecline}
                            data-track="analytics"
                            data-bname={`decline`}
                            disabled={rsdUpReadonlyaccessEn}
                        >
                            {t(`pending.button.decline`)}
                        </Button>
                    }
                    error={error}
                    loading={loading}
                />
            )}
        </TeamContext.Provider>
    );
};

export default MyTeam;
